import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import React from 'react';

import { TIPTAP_POLL_TYPE } from './../ricos-content';
import type { RicosServices, ToolbarButton } from './../ricos-types';
import { PLUGIN_TOOLBAR_BUTTON_ID } from './../editor-common';
import { selectedNodeResolver } from './../plugin-commons';
import { FLOATING_PLUGIN_TOOLBAR } from './../toolbars-v3';

import { SettingsModal } from './components/settings-modal/settings-modal';
import type { SettingsType } from './consts';
import {
  POLL_LAYOUT_DROPDOWN_MODAL_ID,
  POLL_SETTINGS_DESIGN_MODAL_ID,
  POLL_SETTINGS_MODAL_ID,
  SETTINGS_TAB,
} from './consts';
import { PLUGIN_TOOLBAR_DESIGN_BUTTON, PLUGIN_TOOLBAR_LAYOUT_DROPDOWN_BUTTON } from './data-hooks';
import { PollDesignButton } from './toolbar/poll-design-button';
import { PollLayoutDropdown } from './toolbar/poll-layout-dropdown';
import { PollLayoutDropdownButton } from './toolbar/poll-layout-dropdown-button';
import type { PollPluginEditorConfig } from './types';

export const getToolbarButtons = (
  config: PollPluginEditorConfig,
  services: RicosServices
): ToolbarButton[] => {
  const { modals } = services;
  const { isMobile } = services?.context ?? {};

  const openSettingsModal = ({
    modalId,
    node,
    referenceElement,
    activeTab,
  }: {
    modalId: string;
    node: ProseMirrorNode;
    referenceElement: HTMLElement;
    activeTab: SettingsType;
  }) => {
    modals?.togglePluginPanel({
      id: modalId,
      Component: SettingsModal,
      componentProps: {
        nodeId: node.attrs.id,
        activeTab,
        config,
        modalId,
      },
      positioning: {
        referenceElement:
          referenceElement.closest(`[data-hook~=${FLOATING_PLUGIN_TOOLBAR}]`) ?? referenceElement,
        placement: 'right-start',
      },
      focusReturnElement: referenceElement,
      isMobile,
    });
  };

  return [
    {
      id: POLL_LAYOUT_DROPDOWN_MODAL_ID,
      dataHook: PLUGIN_TOOLBAR_LAYOUT_DROPDOWN_BUTTON,
      attributes: {
        layout: selectedLayoutResolver,
        selectedNode: selectedNodeResolver,
      },
      command: ({ referenceElement, isMobile, tiptapEditor, attributes }) => {
        if (modals?.isModalOpen(POLL_LAYOUT_DROPDOWN_MODAL_ID)) {
          modals?.closeModal(POLL_LAYOUT_DROPDOWN_MODAL_ID);
          return;
        }

        modals?.openModal({
          id: POLL_LAYOUT_DROPDOWN_MODAL_ID,
          Component: PollLayoutDropdown,
          componentProps: {
            selectedLayout: attributes?.layout || 'GRID',
            onLayoutClick: layout => {
              tiptapEditor
                .chain()
                .focus()
                .updateAttributes(TIPTAP_POLL_TYPE, {
                  layout: {
                    poll: { ...attributes.selectedNode.attrs.layout.poll, type: layout },
                  },
                })
                .run();

              modals?.closeModal(POLL_LAYOUT_DROPDOWN_MODAL_ID);
            },
            onCustomizeButtonClick: () => {
              openSettingsModal({
                modalId: POLL_SETTINGS_MODAL_ID,
                node: attributes.selectedNode,
                referenceElement,
                activeTab: SETTINGS_TAB.layout,
              });
            },
          },
          layout: isMobile ? 'drawer' : 'toolbar',
          positioning: { referenceElement, placement: 'bottom' },
          returnFocusOnDeactivate: true,
          type: 'exclusive',
        });
      },
      renderer: toolbarItem => <PollLayoutDropdownButton toolbarItem={toolbarItem} />,
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.SEPARATOR,
    },
    {
      id: POLL_SETTINGS_DESIGN_MODAL_ID,
      dataHook: PLUGIN_TOOLBAR_DESIGN_BUTTON,
      command: ({ referenceElement, attributes: { selectedNode } }) => {
        openSettingsModal({
          modalId: POLL_SETTINGS_DESIGN_MODAL_ID,
          node: selectedNode,
          referenceElement,
          activeTab: SETTINGS_TAB.design,
        });
      },
      attributes: {
        selectedNode: selectedNodeResolver,
        active: {
          id: 'IS_POLL_DESIGN_SETTINGS_BUTTON_ACTIVE',
          resolve: (_, { modals }) => Boolean(modals.getModal(POLL_SETTINGS_DESIGN_MODAL_ID)),
        },
      },
      renderer: toolbarItem => <PollDesignButton toolbarItem={toolbarItem} />,
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.SEPARATOR,
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.SETTINGS,
      attributes: {
        active: {
          id: 'IS_POLL_SETTINGS_BUTTON_ACTIVE',
          resolve: (_, { modals }) => Boolean(modals.getModal(POLL_SETTINGS_MODAL_ID)),
        },
      },
      command: ({ referenceElement, attributes: { selectedNode } }) => {
        openSettingsModal({
          modalId: POLL_SETTINGS_MODAL_ID,
          node: selectedNode,
          referenceElement,
          activeTab: SETTINGS_TAB.settings,
        });
      },
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.SEPARATOR,
    },
    {
      id: PLUGIN_TOOLBAR_BUTTON_ID.DELETE,
    },
  ];
};

const selectedLayoutResolver = {
  id: 'selectedPollLayout',
  resolve: (content: $TSFixMe) => {
    const pollNode = Array.isArray(content) && content.find(node => node.type.name === 'POLL');
    if (!pollNode) {
      return undefined;
    }
    return pollNode.attrs.layout?.poll.type;
  },
};
